import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiError, errorMessage, get, post } from '../../api.js';
import { useToast } from '../../components/Toast.js';
import { Card, Field, Notice, PageHead } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import { useSignedIn } from '../../session.js';

interface Preview {
  portal: {
    contacts: number;
    leads: number;
    events: number;
    travel: number;
    expenses: number;
    roiProjects: number;
    packages: number;
  };
  hubspot: { contacts: number; companies: number; deals: number; tasks: number; notes: number };
}

const CONFIRM_WORDS = 'RESET STAGING';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Settings > Reset staging data (round 2; admins, on staging only: the
 * permission exists nowhere else and the server refuses it). Says what goes
 * and what stays, the HubSpot records that will be deleted per type, then
 * asks for RESET STAGING and an authenticator code.
 */
export function StagingReset() {
  const { refresh } = useSignedIn();
  const { sendNow } = useOffline();
  const toast = useToast();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [words, setWords] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let current = true;
    get<Preview>('/admin/staging-reset')
      .then((p) => current && setPreview(p))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [refresh, reload]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (words.trim() !== CONFIRM_WORDS) return setError(`Type ${CONFIRM_WORDS} to confirm.`);
    if (!/^\d{6}$/.test(code)) return setError('Enter the 6-digit code of your authenticator app.');
    setBusy(true);
    setError(null);
    try {
      await post('/admin/staging-reset', { confirm: words, code });
      setWords('');
      setCode('');
      // This device empties its own copy at once; the others at their next sync.
      await sendNow();
      toast('Staging data reset. HubSpot records go within a few minutes.');
      setReload((n) => n + 1);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid_code'
          ? 'This code is not valid. Check the time of your phone and try the next code.'
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  const p = preview?.portal;
  const hs = preview?.hubspot;
  const inHubSpot = hs ? Object.values(hs).reduce((a, b) => a + b, 0) : 0;
  return (
    <>
      <Link to="/settings" className="back">
        Settings
      </Link>
      <PageHead
        title="Reset staging data"
        lede="Empties the staging portal to test again from a clean start. It exists on staging only."
      />
      <Notice kind="warn">This cannot be undone.</Notice>
      {p && hs && (
        <>
          <Card title="Deleted in the portal">
            <ul className="reset-list">
              <li>
                {plural(p.contacts, 'contact')}, {plural(p.leads, 'lead')}, their links and notes,
                To do
              </li>
              <li>
                {plural(p.events, 'event')}, their logistics, notes, booth kiosk content and recaps
              </li>
              <li>{plural(p.travel, 'travel booking')} and their files</li>
              <li>{plural(p.expenses, 'expense')} and their PDFs</li>
              <li>{plural(p.roiProjects, 'ROI project')}, deleted ones included</li>
              <li>{plural(p.packages, 'package')}, their prospect pages and activity</li>
              <li>The Deleted in HubSpot list</li>
              <li>Card pictures, receipts and travel files on Spaces</li>
              <li>What waits to be sent by the worker</li>
            </ul>
          </Card>
          <Card
            title="Deleted in HubSpot (test account)"
            note="Only what the portal created. A record that was in HubSpot before is never deleted, even if the portal used or changed it: it only leaves the portal."
          >
            <ul className="reset-list">
              <li>{plural(hs.contacts, 'contact')}</li>
              <li>{plural(hs.companies, 'company', 'companies')}</li>
              <li>{plural(hs.deals, 'deal')}</li>
              <li>{plural(hs.tasks, 'task')}</li>
              <li>{plural(hs.notes, 'note')}</li>
            </ul>
            {inHubSpot === 0 && <p className="note">Nothing created by the portal in HubSpot.</p>}
          </Card>
          <Card title="Kept">
            <ul className="reset-list">
              <li>Users, roles, the kiosk PIN, business cards and trusted devices</li>
              <li>The content library: videos, PDFs and their files</li>
              <li>The audit log</li>
              <li>The configuration</li>
            </ul>
            <p className="note">
              Devices already signed in empty their own data at their next sync, and stay signed in.
            </p>
          </Card>
          <Card title="Confirm">
            <form className="vstack" noValidate onSubmit={(e) => void submit(e)}>
              <Field
                label={`Type ${CONFIRM_WORDS}`}
                autoComplete="off"
                value={words}
                onChange={(e) => setWords(e.target.value)}
              />
              <Field
                label="Code of your authenticator app"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              {error && <Notice kind="error">{error}</Notice>}
              <div className="actions">
                <button type="submit" className="btn danger" disabled={busy}>
                  Reset staging data
                </button>
              </div>
            </form>
          </Card>
        </>
      )}
      {!preview && error && <Notice kind="error">{error}</Notice>}
      {!preview && !error && <div className="spinner" aria-label="Loading" />}
    </>
  );
}
